import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import makeComponentTrashable from "trashable-react";
import { useStatusMessage, useTranslation } from "Shared/util/custom-hooks";
import { updateDefaultOrg } from "Shared/util/web-api";
import { joinClassNames as c } from "Shared/util/styles";
import StatusMessage from "Shared/components/status-message";
import ReturnTitle from "Shared/components/return-title";
import useUserContext from "Contexts/user-context";
import Button from "Shared/components/button";
import { Select } from "Shared/components/select";
import tabStyles from "./tab.css";
import styles from "./select-default-org-tab.css";

const SelectDefaultOrgTab = ({ registerPromise, display, onHide }) => {
    const [statusMessage, dispatchStatusMessage] = useStatusMessage();
    const [organisationItems, setOrganisationItems] = useState([]);
    const [sessionDefaultOrganisationUgId, setSessionDefaultOrganisationUgId] =
        useState();
    const [selectedUgId, setSelectedUgId] = useState();
    const t = useTranslation((x) => x.tabs);
    const tg = useTranslation((x) => x.global);
    const { user, loading, updateUserSessionToken } = useUserContext();

    useEffect(() => {
        if (loading) {
            return;
        }

        const { id } = user.defaultCompany;
        setSelectedUgId(id);
        setSessionDefaultOrganisationUgId(id);

        if (display) {
            const organisations = user.companies || [];
            const mappedOrganisations = organisations.map((co) => ({
                label: co.name,
                value: co.id,
            }));
            setOrganisationItems(mappedOrganisations);
        } else {
            dispatchStatusMessage({ type: "NONE" });
        }
    }, [display, loading]);

    const handleSubmitForm = (e) => {
        e.preventDefault();

        registerPromise(updateDefaultOrg(selectedUgId))
            .then((result) => {
                updateUserSessionToken(result);
                onHide();
            })
            .catch(() =>
                dispatchStatusMessage({
                    type: "FAILED",
                    message: t.managedCompanyCouldNotBeUpdated,
                }),
            );
    };

    const handleClearStatusMessage = () =>
        dispatchStatusMessage({ type: "NONE" });

    const handleOrganisationChanged = (value) => setSelectedUgId(value);

    return (
        <div
            className={c(
                tabStyles.container,
                display ? tabStyles.visible : tabStyles.hidden,
            )}
        >
            <StatusMessage
                status={statusMessage.type}
                text={statusMessage.message}
                clearStatus={handleClearStatusMessage}
                className={styles.statusMessageContainer}
            />
            <ReturnTitle title={t.defaultCompany} onClick={onHide} display />
            <form className={tabStyles.content} onSubmit={handleSubmitForm}>
                <p>{t.selectTheCompanyYouWantToUse}</p>
                <Select
                    label={t.defaultCompany}
                    items={organisationItems}
                    selectedValue={selectedUgId}
                    onChange={handleOrganisationChanged}
                    className={styles.companySelectContainer}
                />
                <div className={tabStyles.buttonsContainer}>
                    <Button
                        buttonType="button-secondary"
                        text={tg.cancel}
                        onClick={onHide}
                    />
                    <Button
                        text={t.enterDefault}
                        attributes={{
                            type: "submit",
                            disabled:
                                selectedUgId === sessionDefaultOrganisationUgId,
                        }}
                        className={tabStyles.submitButton}
                    />
                </div>
            </form>
        </div>
    );
};

SelectDefaultOrgTab.propTypes = {
    registerPromise: PropTypes.func.isRequired,
    display: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
};

export default makeComponentTrashable(SelectDefaultOrgTab);
